import React, { useState, useEffect } from "react";
import "../../../css/AddStationModal.css";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';


interface Role {
  _id: string;
  name: string;
  parent: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddStationModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [roles, setRoles] = useState<Role[]>([]);
  const [subroles, setSubroles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedSubroleId, setSelectedSubroleId] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setName("");
    setDescription("");
    setSelectedRoleId("");
    setSelectedSubroleId("");
    setSubroles([]);
  };

  // ✅ Fetch roles on mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) return;

        const res = await api.auth_api.get("/roles", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const topLevelRoles = res.data.filter(
          (role: Role) => role.parent === null
        );
        setRoles(topLevelRoles);
      } catch (err) {
        console.error("❌ Error fetching roles:", err);
      }
    };

    if (isOpen) {
      resetForm();
      fetchRoles();
    }
  }, [isOpen]);

  // ✅ Fetch subroles whenever a role is selected
  useEffect(() => {
    const fetchSubroles = async () => {
      if (!selectedRoleId) {
        setSubroles([]);
        return;
      }

      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) return;

        const res = await api.auth_api.get(`/roles/${selectedRoleId}/subroles`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setSubroles(res.data || []);
      } catch (err) {
        console.error("❌ Error fetching subroles:", err);
      }
    };

    fetchSubroles();
  }, [selectedRoleId]);

  // ✅ Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        showToast("Authentication error: No access token found.", 'error');
        setIsSubmitting(false);
        return;
      }
      
      const selectedRole = roles.find((role) => role._id === selectedRoleId);

      // ✅ 1. Start with the required data
      const stationData: any = {
        name,
        description,
        role: selectedRoleId,
        roleName: selectedRole?.name,
      };

      // ✅ 2. Conditionally add sub-role data ONLY if it's selected
      if (selectedSubroleId) {
        const selectedSubrole = subroles.find(
          (subrole) => subrole._id === selectedSubroleId
        );
        stationData.subrole = selectedSubroleId;
        stationData.subroleName = selectedSubrole?.name;
      }

      // Now the `subrole` key won't exist in the payload if it's not selected
      await api.center_api.post("/api/v1/stations/stations", stationData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showToast("✅ Station added successfully!", 'success');
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      console.error("❌ Failed to add station:", error);
      showToast("Error: Could not add the station. Please try again.", 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // ✅ Handler to reset sub-role when role changes
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRoleId(e.target.value);
    setSelectedSubroleId(""); // Reset sub-role selection
  };


  if (!isOpen) return null;

  return (
    <div className="add-station-modal-overlay" onClick={onClose}>
      <div className="add-station-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-station-modal-header">
          <h2>Add New Station</h2>
          <button className="add-station-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-station-modal-form">
          <label>Station Name</label>
          <input
            type="text"
            placeholder="Enter station name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <label>Description</label>
          <textarea
            placeholder="Enter station description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <label>Assign Role</label>
          <select
            value={selectedRoleId}
            onChange={handleRoleChange} // Use the new handler
            required
          >
            <option value="">Select a role</option>
            {roles.map((role) => (
              <option key={role._id} value={role._id}>
                {role.name}
              </option>
            ))}
          </select>

          <label>Assign Sub-Role (Optional)</label>
          <select
            value={selectedSubroleId}
            onChange={(e) => setSelectedSubroleId(e.target.value)}
            disabled={!selectedRoleId || subroles.length === 0} // Also disable if no subroles exist
          >
            <option value="">Select a Sub-role</option>
            {subroles.map((subrole) => (
              <option key={subrole._id} value={subrole._id}>
                {subrole.name}
              </option>
            ))}
          </select>

          <button type="submit" className="add-station-add-btn" disabled={isSubmitting}>
            {isSubmitting ? "Adding..." : "Add Station"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddStationModal;